import { getSavedBandwidthEstimate } from '~/composables/useHlsPlayer/shakaLoader';

/**
 * Central policy for SPECULATIVE downloads (content the user has not asked
 * for yet). Confirmed intent (pointerdown/click, the shorts route itself)
 * never consults this — those bytes would be fetched moments later anyway.
 *
 * Three dimensions, decided here in one place:
 * - WHEN: only on fast networks (>= 4 Mbps estimated), outside Data Saver,
 *   with the page visible.
 * - HOW MUCH: a per-session byte budget shared by every speculative
 *   mechanism, so their sum stays bounded no matter how many surfaces mount.
 * - WHAT: one section per page holds the warm right at a time (the first to
 *   claim while visible; scrolling to another transfers it), so N carousels
 *   never multiply the spend.
 */

const SPECULATIVE_BUDGET_BYTES = 500_000;
const SPECULATIVE_MIN_BPS = 4_000_000;

// Approximate costs — accounting, not measurement: a warm is charged when it
// starts, since aborting mid-flight still consumed most of the bytes.
export const WARM_COST_MANIFESTS = 10_000;
export const WARM_COST_SEGMENT = 250_000;
export const WARM_COST_FEED_API = 10_000;

let spentBytes = 0;
let activeSectionId: string | null = null;

/** Gates that apply to every speculative download, regardless of budget. */
export function canSpeculate(): boolean {
	if (!import.meta.client) return false;
	try {
		if (document.visibilityState === 'hidden') return false;
		const connection = (
			navigator as Navigator & { connection?: { saveData?: boolean } }
		).connection;
		if (connection?.saveData) return false;
		const bps = getSavedBandwidthEstimate();
		return bps !== null && bps >= SPECULATIVE_MIN_BPS;
	} catch {
		return false;
	}
}

/**
 * Charges a speculative download against the session budget. Returns false
 * (and charges nothing) once the budget is exhausted — the caller simply
 * skips the warm; confirmed intent still downloads normally.
 */
export function trySpendWarmBudget(costBytes: number): boolean {
	if (spentBytes + costBytes > SPECULATIVE_BUDGET_BYTES) return false;
	spentBytes += costBytes;
	return true;
}

/**
 * The warm right belongs to one section per page. First visible section
 * claims it; a later section claims successfully only after the holder
 * releases (scrolled away/unmounted). Claiming is idempotent per id.
 */
export function claimWarmSection(sectionId: string): boolean {
	if (activeSectionId === null || activeSectionId === sectionId) {
		activeSectionId = sectionId;
		return true;
	}
	return false;
}

export function releaseWarmSection(sectionId: string): void {
	if (activeSectionId === sectionId) activeSectionId = null;
}

/** Test hook: budget and claim are module state that outlives components. */
export function resetWarmSchedulerForTests(): void {
	spentBytes = 0;
	activeSectionId = null;
}
